import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Milestone 2 Phase 2 · Scope Context — company + division authorization + active
 * scope switcher used by the header.
 */
const ScopeCtx = createContext(null);

const LS_KEY = "dh_active_scope_v1";

export function ScopeProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [activeDivisionId, setActiveDivisionId] = useState(null);
  const [canPrepareQuotations, setCanPrepareQuotations] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setCompanies([]); setDivisions([]);
      setActiveCompanyId(null); setActiveDivisionId(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    api.get("/administration/my-scope").then((r) => {
      if (cancel) return;
      const data = r.data || {};
      setCompanies(data.companies || []);
      setDivisions(data.divisions || []);
      setCanPrepareQuotations(!!data.can_prepare_quotations);
      // Restore last active from LS, fallback to first authorized.
      // NOTE: LS_KEY holds only non-sensitive UI preference (company/division IDs
      // are lookup handles, not auth material). Auth tokens live in httpOnly cookies.
      let saved = null;
      try { saved = JSON.parse(window.localStorage.getItem(LS_KEY) || "null"); }
      catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[scope] LS parse failed", err); }
      const firstCid = (data.companies || [])[0]?.id || null;
      const firstDid = (data.divisions || [])[0]?.id || null;
      const cid = (data.companies || []).some((c) => c.id === saved?.cid) ? saved.cid : firstCid;
      const did = (data.divisions || []).some((d) => d.id === saved?.did) ? saved.did : firstDid;
      setActiveCompanyId(cid);
      setActiveDivisionId(did);
    }).catch(() => {
      setCompanies([]); setDivisions([]);
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // Only re-run when the caller changes; api + LS_KEY are stable module bindings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    if (activeCompanyId || activeDivisionId) {
      try {
        window.localStorage.setItem(LS_KEY, JSON.stringify({ cid: activeCompanyId, did: activeDivisionId }));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("[scope] LS write failed", err);
      }
    }
  }, [activeCompanyId, activeDivisionId]);

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === activeCompanyId) || null,
    [companies, activeCompanyId],
  );
  const activeDivision = useMemo(
    () => divisions.find((d) => d.id === activeDivisionId) || null,
    [divisions, activeDivisionId],
  );

  const value = useMemo(() => ({
    companies, divisions,
    activeCompany, activeDivision,
    activeCompanyId, activeDivisionId,
    setActiveCompanyId, setActiveDivisionId,
    canPrepareQuotations, loading,
  }), [companies, divisions, activeCompany, activeDivision, activeCompanyId, activeDivisionId, canPrepareQuotations, loading]);

  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

export function useScope() {
  const v = useContext(ScopeCtx);
  if (!v) throw new Error("useScope must be inside ScopeProvider");
  return v;
}
